import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import Stepper from "@/components/Stepper"
import { ConfettiModal, WarningModal } from "@/components/SubmitResultModals"
import { useSelfQuery } from "@/data/queries"
import {
  useCreateBloodGlucoseFormMutation,
  useObservationByIdQuery,
} from "@/data/queries/measurementQueries"
import { toMedicationLog, type MedicationTaken } from "@/model/BloodGlucoseForm"
import { hasPrescribedMedicationForContext } from "@/model/Medication"
import { ROUTES } from "@/routes"
import { useState } from "react"
import { Navigate, useLocation, useNavigate } from "react-router"
import { toast } from "sonner"
import MedicationQuestionStep from "./steps/MedicationQuestionStep"
import NoteScreenStep from "./steps/NoteScreenStep"

const NOTE_CONTEXT = "Fasting"

const CRITICAL_CLASSIFICATIONS = new Set(["emergency-low", "critical-low", "critical-high"])

type WarningState = { reading: number | null; message: string | null }

const FastingMeasurementPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as { readingId?: string; initialTab?: string } | null
  const readingId = state?.readingId ?? null
  const dashboardReturnUrl = `${ROUTES.DASHBOARD}?tab=${state?.initialTab ?? "overview"}`
  const { data: self } = useSelfQuery()
  const patientId = self?.id ?? ""
  const { mutateAsync: createForm, isPending } = useCreateBloodGlucoseFormMutation({ patientId })
  const { data: observation } = useObservationByIdQuery({ readingId })

  const [warning, setWarning] = useState<WarningState | null>(null)
  const [confettiOpen, setConfettiOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [medicationTaken, setMedicationTaken] = useState<MedicationTaken | null>(null)
  const [medicationNote, setMedicationNote] = useState("")

  if (!readingId) {
    return <Navigate to={`${ROUTES.DASHBOARD}?tab=overview`} replace />
  }

  const onMedication = (values: { medicationTaken: MedicationTaken | null; note: string }) => {
    setMedicationTaken(values.medicationTaken)
    setMedicationNote(values.note)
    setCurrentStep((step) => step + 1)
  }

  const onNotes = async ({ notes }: { notes: string }) => {
    try {
      await createForm({
        patientId,
        form: {
          context: "fasting",
          dateTime: null,
          notes: notes || null,
          carbsInGrams: null,
          proteinInGrams: null,
          mealContents: null,
          medications: toMedicationLog(medicationTaken, medicationNote),
          reasons: null,
          readingId,
        },
      })
      const classification = observation?.reading?.classification
      const reading = observation?.reading?.bloodGlucoseInMilligramsPerDeciliter
      if (classification && CRITICAL_CLASSIFICATIONS.has(classification)) {
        setWarning({
          reading: reading ?? null,
          message: observation?.note ?? null,
        })
      } else {
        setConfettiOpen(true)
      }
    } catch (error) {
      const isLocked = error instanceof Error && /423/.test(error.message)
      toast.error(
        isLocked
          ? "Your doctor has already reviewed your measurement. It is no longer possible to submit the form"
          : "There was a problem submitting the form. Please try again later, or contact Bloomlife Support",
      )
    }
  }

  const dismissAndGoToDashboard = () => {
    setWarning(null)
    setConfettiOpen(false)
    navigate(dashboardReturnUrl)
  }

  if (!self) {
    return (
      <Page showAppbar={false}>
        <div className="flex items-center justify-center p-8">
          <Spinner />
        </div>
      </Page>
    )
  }

  const showMedicationStep = hasPrescribedMedicationForContext(
    self.prescribedDiabetesMedications,
    "fasting",
  )

  return (
    <Page showAppbar={false}>
      <Stepper
        currentStep={currentStep}
        steps={[
          ...(showMedicationStep
            ? [
                <MedicationQuestionStep
                  key="medication"
                  medicationTaken={medicationTaken}
                  note={medicationNote}
                  onSubmit={onMedication}
                  onBack={() => navigate(dashboardReturnUrl)}
                />,
              ]
            : []),
          <NoteScreenStep
            key="notes"
            context={NOTE_CONTEXT}
            isLoading={isPending}
            onSubmit={onNotes}
            onBack={() => (showMedicationStep ? setCurrentStep(0) : navigate(dashboardReturnUrl))}
          />,
        ]}
      />

      <WarningModal
        open={warning !== null}
        reading={warning?.reading ?? null}
        message={warning?.message ?? null}
        onClose={dismissAndGoToDashboard}
      />
      <ConfettiModal open={confettiOpen} onDismiss={dismissAndGoToDashboard} />
    </Page>
  )
}

export default FastingMeasurementPage
